import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Form, message } from 'antd';
import styles from './styles/userReport.module.less';
import { useAppConfig } from '@state';
import { utils } from '@ohif/core';
import ValidateModal from './ValidateModal';
import { normalizeDataIndex, formatStudyDate, getStateText } from '../../../utils/reportHelper';

const { apiService } = utils;

// 定义 StudyData 类型
interface StudyData {
  register_key?: string;
  yearMonthDay?: string;
  bodypart_name?: string;
  modality_code?: string;
  chinese_name?: string;
  sex?: string;
  age?: string;
  study_time?: string;
  check_view2?: string;
  diagnosis_result2?: string;
  state?: string;
  stateText?: string;
  index?: number;
}

const UserReport = React.memo(
  () => {
    const [appConfig] = useAppConfig();
    const navigate = useNavigate();
    const location = useLocation();
    const [studies, setStudies] = useState<StudyData[]>([]);
    const [listLoading, setListLoading] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();
    const [form] = Form.useForm();
    const tokenManager = (window as any).tokenManager;

    // 用ref跟踪加载状态，避免作为依赖导致循环
    const listLoadingRef = useRef(listLoading);
    useEffect(() => {
      listLoadingRef.current = listLoading;
    }, [listLoading]);

    // 静态参数缓存
    const paramKeys = useMemo(
      () => [
        'study_id',
        'patient_no',
        'patient_id',
        'in_no',
        'out_no',
        'identification_no',
        'ybkh',
        'jzkh',
        'idCard',
        'register_key',
      ],
      []
    );

    const firstParamValue = useMemo(() => {
      const queryParams = new URLSearchParams(location.search);
      for (const key of paramKeys) {
        const value = queryParams.get(key);
        if (value) return value;
      }
      return 'default';
    }, [location.search, paramKeys]);

    const storageKey = useMemo(
      () => `isValidate_${firstParamValue || 'default'}`,
      [firstParamValue]
    );

    const [authState, setAuthState] = useState({
      isValidate: !!localStorage.getItem(storageKey),
      modalVisible: !localStorage.getItem(storageKey) || !tokenManager.getCurrentToken(),
      isSubmitting: false,
    });

    // 数据获取函数 - 移除listLoading依赖，解决循环问题
    const getData = useCallback(
      async (params: any = {}) => {
        // 使用ref判断加载状态，而非直接依赖state
        if (listLoadingRef.current) return;
        const formData = new FormData();
        let paramsCount = 0;
        for (const key in params) {
          if (paramKeys.includes(key)) {
            formData.append(key, params[key]);
            paramsCount++;
          }
        }
        if (paramsCount === 0) {
          return;
        }
        formData.append('cloud_image', '1');
        formData.append('pageSize', '100');
        setListLoading(true);

        try {
          const res = await apiService({
            uri: '/api/reglist/stream-page',
            method: 'POST',
            data: formData,
            type: null,
          });

          if (!res?.Data) throw new Error('检查列表数据为空');

          let data = res.Data.map((item: any, index: number) => ({ ...item, index: index + 1 }));
          let normalizedData = normalizeDataIndex(data, true);

          normalizedData.sort((a, b) => {
            const parseDate = (dateString: string) => {
              if (!dateString) return -Infinity;
              const date = new Date(dateString);
              return isNaN(date.getTime()) ? -Infinity : date.getTime();
            };
            return parseDate(b.study_time) - parseDate(a.study_time);
          });

          normalizedData = normalizedData.map((item: any) => ({
            ...item,
            yearMonthDay: item.study_time ? formatStudyDate(item.study_time).yearMonthDay : '',
            stateText: item.state ? getStateText(item.state) : '',
          }));

          setStudies(normalizedData);
        } catch (error) {
          setStudies([]);
          messageApi.error(
            `获取检查列表失败: ${error instanceof Error ? error.message : '未知错误'}`
          );
        } finally {
          setListLoading(false);
        }
      },
      [messageApi, paramKeys] // 仅保留必要依赖
    );

    // 验证弹窗确定按钮处理
    const handleOK = useCallback(async () => {
      if (authState.isSubmitting) return;
      setAuthState(prev => ({ ...prev, isSubmitting: true }));

      try {
        const values = await form.validateFields();
        const idCard = values.idCard;
        const formData = new FormData();
        formData.append('idCard', idCard);

        const queryParams = new URLSearchParams(location.search);
        for (const [key, value] of queryParams.entries()) {
          formData.append(key, value);
        }

        const res = await apiService({
          uri: '/api/login/CloudVerify',
          method: 'POST',
          data: formData,
          type: null,
        });

        if (res.Status === '0' || res.Status === 0) {
          setAuthState({
            isValidate: true,
            modalVisible: false,
            isSubmitting: false,
          });
          localStorage.setItem(storageKey, 'true');
          if (tokenManager && tokenManager.setToken) {
            tokenManager.setToken(res.Token, res.ExpiresUtc, res.RefreshToken);
          }
          // 清除其他验证状态
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('isValidate_') && key !== storageKey) {
              localStorage.removeItem(key);
            }
          });
          messageApi.success('身份验证成功');
        } else if (res.Status === '1' || res.Status === 1) {
          setAuthState(prev => ({ ...prev, isSubmitting: false }));
          setTimeout(() => messageApi.error('验证失败，请检查手机号'), 0);
        } else {
          setAuthState(prev => ({ ...prev, isSubmitting: false }));
          setTimeout(() => messageApi.error('未知的验证响应，请重试'), 0);
        }
      } catch (error) {
        setAuthState(prev => ({ ...prev, isSubmitting: false }));
        setTimeout(() => {
          messageApi.error(error instanceof Error ? error.message : '验证过程出错，请重试');
        }, 0);
      }
    }, [form, messageApi, storageKey, location.search, authState.isSubmitting]);

    // 查看详情处理
    const handleShowDetail = useCallback(
      (item: StudyData) => {
        try {
          if (!item.register_key) {
            messageApi.error('缺少唯一标识，无法查看详情');
            return;
          }
          const currentSearchParams = new URLSearchParams(location.search);
          currentSearchParams.set('register_key', item.register_key);
          currentSearchParams.set('from', 'userreport');
          navigate(`/reportdetail?${currentSearchParams.toString()}`);
        } catch (error) {
          messageApi.error('跳转失败，请重试');
        }
      },
      [messageApi, location.search, navigate]
    );

    useEffect(() => {
      console.log('tokenManager.getCurrentToken', tokenManager.getCurrentToken());
      // 仅当验证通过且不在加载中时才调用接口
      if (authState.isValidate && !listLoadingRef.current && tokenManager.getCurrentToken()) {
        const params = Object.fromEntries(new URLSearchParams(location.search).entries());
        getData({ ...params, param: firstParamValue });
      }
    }, [authState.isValidate, firstParamValue, location.search, getData, authState.modalVisible]);

    useEffect(() => {
      const onUnauthorized = () => {
        setStudies([]);
        setAuthState(prev => ({
          ...prev,
          isValidate: false,
          modalVisible: true,
          isSubmitting: false,
        }));
      };
      window.addEventListener('token:unauthorized', onUnauthorized as EventListener);
      return () => {
        window.removeEventListener('token:unauthorized', onUnauthorized as EventListener);
      };
    }, []);

    return (
      <>
        {contextHolder}
        <div
          className={styles.container}
          id="userreport"
        >
          <h3 className={styles.title}>我的云胶片</h3>
          <div className={styles.content}>
            <div className={styles.header}>
              <h3 className={styles.sectionTitle}>云胶片</h3>
              <p>如果您已经做了检查且报告已出，但页面无数据，请联系工作人员。</p>
            </div>
            <div className={styles.listWrapper}>
              {listLoading ? (
                <p>加载中...</p>
              ) : studies.length > 0 ? (
                <ul className={styles.reportList}>
                  {studies.map((item, index) => (
                    <li
                      key={item.register_key || index}
                      className={styles.reportItem}
                      onClick={() => handleShowDetail(item)}
                    >
                      <h3 className={styles.itemDate}>{item.yearMonthDay}</h3>
                      <div className={styles.itemDetails}>
                        <h3 className={styles.itemTitle}>{item.modality_code}</h3>
                        <hr className={styles.divider} />
                        <h3 className={styles.itemTitle}>{item.bodypart_name}</h3>
                        <hr className={styles.divider} />
                        <p className={styles.hospital}>{appConfig.hospitalName}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>暂无数据</p>
              )}
            </div>
          </div>
          <ValidateModal
            visible={authState.modalVisible}
            isSubmitting={authState.isSubmitting}
            onOk={handleOK}
            form={form}
          />
        </div>
      </>
    );
  },
  () => true
);

UserReport.displayName = 'UserReport';

export default UserReport;
